/**
 * CTO Directive #126.2 §P0-D — Smart auto-scroll for Living Conversation.
 *
 * Rules:
 *   • When a new message arrives AND the user is near the bottom of
 *     the conversation, scroll smoothly to the bottom.
 *   • If the user has scrolled UP to read history, DO NOT force them
 *     back down. Instead expose ``newBelow`` so the parent can render
 *     a small "Yeni yanıt var — en alta dön" pill.
 *   • The pill is a hint, not a modal or blocking overlay.
 *   • SSE token-by-token growth in an active response must not bounce
 *     the screen — the container's height stays visually stable via
 *     the near-bottom threshold.
 *
 * Usage:
 *   const {
 *     scrollRef, bottomRef, newBelow, scrollToBottom, isNearBottom,
 *   } = useSmartAutoScroll({ deps: [messages.length, streamingSuffix], nearBottomPx: 120 });
 *
 *   return (
 *     <div ref={scrollRef} className="scroll">
 *       {messages...}
 *       <div ref={bottomRef} />
 *     </div>
 *   );
 */
import { useCallback, useEffect, useRef, useState } from "react";

export function useSmartAutoScroll({
  deps = [],
  nearBottomPx = 120,
  behavior = "smooth",
} = {}) {
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const [newBelow, setNewBelow] = useState(false);

  const isNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
    return distance <= nearBottomPx;
  }, [nearBottomPx]);

  const scrollToBottom = useCallback((mode = behavior) => {
    const el = scrollRef.current;
    if (!el) return;
    // Use scrollIntoView when possible for smoother behaviour on modern browsers.
    if (bottomRef.current && typeof bottomRef.current.scrollIntoView === "function") {
      try {
        bottomRef.current.scrollIntoView({ behavior: mode, block: "end" });
      } catch {
        el.scrollTop = el.scrollHeight;
      }
    } else {
      el.scrollTop = el.scrollHeight;
    }
    setNewBelow(false);
  }, [behavior]);

  // Detect when the user manually scrolls; clear "newBelow" when they
  // return to the bottom themselves.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = () => {
      if (isNearBottom()) setNewBelow(false);
    };
    el.addEventListener("scroll", handler, { passive: true });
    return () => el.removeEventListener("scroll", handler);
  }, [isNearBottom]);

  // When deps change (new message / streaming suffix), decide whether
  // to follow the tail or drop a pill.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isNearBottom()) {
      scrollToBottom("smooth");
    } else {
      setNewBelow(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { scrollRef, bottomRef, newBelow, scrollToBottom, isNearBottom };
}
